// AI vs AI battle resolution system
// Handles automatic battles between AI-controlled teams

import { GameState, getTeamsInDivision, getCurrentMonthType } from './GameState';
import { TeamData, modifyMoney } from '../entities/Team';
import { GladiatorData, isHealthy, getMeleeDmg } from '../entities/Gladiator';
import { MONTH_TYPE, GAME_CONSTANTS } from '../data/constants';
import { random, rollDiceStr } from '../utils/dice';

interface SimplifiedBattle {
  team1: TeamData;
  team2: TeamData;
  winner: TeamData | null;
  loser: TeamData | null;
  team1Kills: number;
  team2Kills: number;
}

// Run all AI vs AI battles for the current month
export function runAIBattles(state: GameState): void {
  const monthType = getCurrentMonthType(state);

  if (monthType === MONTH_TYPE.DIVISION_BATTLE) {
    runDivisionBattles(state);
  } else if (monthType === MONTH_TYPE.CUP_BATTLE) {
    runCupBattles(state);
  }
}

// Run division round-robin battles
function runDivisionBattles(state: GameState): void {
  for (let div = 0; div < GAME_CONSTANTS.DIVISIONS; div++) {
    const teams = getTeamsInDivision(state, div);
    const aiTeams = teams.filter(t => t.playerId === null);

    // Simple round-robin: pair teams
    for (let i = 0; i < aiTeams.length; i += 2) {
      if (i + 1 < aiTeams.length) {
        const result = resolveAIBattle(state, aiTeams[i], aiTeams[i + 1]);
        applyBattleResults(state, result);
      }
    }
  }
}

// Run cup tournament battles
function runCupBattles(state: GameState): void {
  // Simplified: just run some random AI vs AI battles
  const allTeams = Array.from(state.teams.values());
  const aiTeams = allTeams.filter(t => t.playerId === null);

  // Shuffle and pair
  const shuffled = aiTeams.sort(() => random(2) - 1);
  for (let i = 0; i < shuffled.length; i += 2) {
    if (i + 1 < shuffled.length) {
      const result = resolveAIBattle(state, shuffled[i], shuffled[i + 1]);
      applyBattleResults(state, result);
    }
  }
}

// Resolve a battle between two AI teams (simplified simulation)
function resolveAIBattle(state: GameState, team1: TeamData, team2: TeamData): SimplifiedBattle {
  // Get gladiators for each team
  const gladiators1 = getTeamGladiators(state, team1);
  const gladiators2 = getTeamGladiators(state, team2);

  if (gladiators1.length === 0 && gladiators2.length === 0) {
    // Draw - random winner
    const winner = random(2) === 0 ? team1 : team2;
    const loser = winner === team1 ? team2 : team1;
    return { team1, team2, winner, loser, team1Kills: 0, team2Kills: 0 };
  }

  if (gladiators1.length === 0) {
    return { team1, team2, winner: team2, loser: team1, team1Kills: 0, team2Kills: 0 };
  }

  if (gladiators2.length === 0) {
    return { team1, team2, winner: team1, loser: team2, team1Kills: 0, team2Kills: 0 };
  }

  // Calculate team strengths
  const strength1 = calculateTeamStrength(gladiators1);
  const strength2 = calculateTeamStrength(gladiators2);

  // Add randomness
  const roll1 = strength1 + random(50);
  const roll2 = strength2 + random(50);

  // Simulate casualties — knockouts of team1 are kills by team2 and vice versa
  const team2Kills = simulateCasualties(gladiators1, roll2 > roll1);
  const team1Kills = simulateCasualties(gladiators2, roll1 > roll2);

  if (roll1 > roll2) {
    return { team1, team2, winner: team1, loser: team2, team1Kills, team2Kills };
  } else if (roll2 > roll1) {
    return { team1, team2, winner: team2, loser: team1, team1Kills, team2Kills };
  } else {
    // Tie - random winner
    const winner = random(2) === 0 ? team1 : team2;
    const loser = winner === team1 ? team2 : team1;
    return { team1, team2, winner, loser, team1Kills, team2Kills };
  }
}

// Get healthy gladiators for a team (up to MAX_GLADIATORS_IN_BATTLE)
function getTeamGladiators(state: GameState, team: TeamData): GladiatorData[] {
  const gladiators: GladiatorData[] = [];
  for (const gId of team.gladiatorIds) {
    const g = state.gladiators.get(gId);
    if (g && isHealthy(g) && gladiators.length < GAME_CONSTANTS.MAX_GLADIATORS_IN_BATTLE) {
      gladiators.push(g);
    }
  }
  return gladiators;
}

// Calculate team fighting strength
function calculateTeamStrength(gladiators: GladiatorData[]): number {
  let strength = 0;
  for (const g of gladiators) {
    // Base stats contribution
    strength += g.str * 2;
    strength += g.speed;
    strength += g.maxHp;

    // Weapon damage contribution
    const dmg = getMeleeDmg(g);
    strength += rollDiceStr(dmg);

    // Best melee skill
    const bestMeleeAtk = Math.max(...g.skills.meleeAtk);
    strength += bestMeleeAtk;
  }
  return strength;
}

// Simulate casualties from battle, returns number of knockouts inflicted by the opposing team
function simulateCasualties(gladiators: GladiatorData[], wasLosingTeam: boolean): number {
  const casualtyChance = wasLosingTeam ? 40 : 20;
  let knockouts = 0;

  for (const g of gladiators) {
    if (random(100) < casualtyChance) {
      // Gladiator is injured
      g.hurt = 1 + random(2); // 1-2 months injury
      knockouts++;
      console.log(`${g.name} injured for ${g.hurt} months`);
    }
  }

  return knockouts;
}

// Apply battle results (wins, money, kills, etc.)
function applyBattleResults(state: GameState, result: SimplifiedBattle): void {
  if (!result.winner || !result.loser) return;

  // Update win/loss counts
  result.winner.wins++;
  result.loser.losses++;

  // Winner gets money reward
  const reward = 200 + random(100);
  modifyMoney(result.winner, reward);

  // Update ratings
  result.winner.rating = Math.min(100, result.winner.rating + 2);
  result.loser.rating = Math.max(0, result.loser.rating - 1);

  // Arena points
  result.winner.arenaPoints += 10;

  // Distribute kills among team gladiators
  distributeKills(state, result.team1, result.team1Kills);
  distributeKills(state, result.team2, result.team2Kills);

  console.log(`AI Battle: ${result.winner.name} defeated ${result.loser.name}, earned ${reward} kr`);
}

// Distribute kill credit among a team's gladiators
function distributeKills(state: GameState, team: TeamData, kills: number): void {
  if (kills <= 0) return;

  const gladiators = getTeamGladiators(state, team);
  if (gladiators.length === 0) return;

  // Spread kills randomly among healthy gladiators
  for (let i = 0; i < kills; i++) {
    const g = gladiators[random(gladiators.length)];
    g.takeOuts1++;
    g.takeOuts2++;
  }
}
